import styles from './RoleModal.module.scss';
import { Text } from '../../typography';
import Modal from '../Modal';
import { useModal } from '../../../providers/ModalProvider';
import { useTranslation } from 'react-i18next';
import { modals } from '../../../providers/modals';
import { keys } from '../../../utilities/translator/translation_keys';
import { Form } from 'react-bootstrap';
import { loader } from 'graphql.macro';
import { useMutation, useQuery } from '@apollo/client';
import Button from '../../Button';
import Icon from '../../Icon';
import { useEffect, useRef, useState } from 'react';
import PermissionManager from '../../permission_manager/PermissionManager';
import { useCurrentUser } from '../../../providers/UserProvider';
import CreatableSelect from 'react-select/creatable';
import UserMultiSelector from '../../UserMultiSelector';
import { useRoleMutations } from '../../../hooks/roles/role_mutations';
import Spacer from '../../typography/Spacer';
import UniversalDropdown from '../../dropdownV2/UniversalDropdown';
import useCheckPermission from '../../../providers/UsePermissionHook';
import { actions } from '../../../utilities';

const addRoleMutation = loader('src/hooks/roles/roles.add.graphql');

const addRequirementMutation = loader(
  'src/hooks/roles/requirement.add.graphql',
);

const associateRoleRequirementMutation = loader(
  'src/hooks/roles/roles.addRequirement.graphql',
);

const rolesQuery = loader('src/hooks/roles/roles.fetch.graphql');

const deleteRoleMutation = loader('./RoleModal.deleteRole.graphql');

const deleteRequirementMutation = loader(
  'src/hooks/roles/roles.deleteRequirement.graphql',
);

export default function RoleModal() {
  const { isAdmin, allRoles } = useCurrentUser();
  const { modalState, closeModal, updateModal, openConfirmationModal } =
    useModal();
  // The `readOnly` prop is caller-supplied (some callers, like Certifications,
  // never set it at all). Backing these with a real permission check inside
  // the modal itself means it can't be forgotten by a future caller — same
  // reasoning as the Permissions Manager page guard.
  const canManageRequirements = useCheckPermission({
    model: 'requirement',
    action: actions.create,
  });
  const canManageRolePermissions = useCheckPermission({
    model: 'role',
    action: actions.update,
  });
  const canCreateRole = useCheckPermission({
    model: 'role',
    action: actions.create,
  });
  const { t } = useTranslation();
  const [addRole] = useMutation(addRoleMutation);
  const [addRequirement] = useMutation(addRequirementMutation);
  const [associateRoleRequirement] = useMutation(
    associateRoleRequirementMutation,
  );
  const [disassociateRoleRequirement] = useMutation(deleteRequirementMutation);
  const [deleteRole] = useMutation(deleteRoleMutation);
  const { addRoleToUser, removeRoleFromUser, editRole } = useRoleMutations();

  const roleTitleRef = useRef(null);
  const roleDescriptionRef = useRef(null);

  const update = (variables) =>
    updateModal({
      modalName: modals.role,
      variables: { ...variables },
    });

  const {
    roleId,
    show,
    roleTitle,
    roleDescription,
    rolePermissions,
    readOnly,
    title,
    refetch: refetchTable = () => {},
  } = modalState.roleModal;

  // Combines the caller-supplied `readOnly` with the real permission for
  // whichever mode this modal is in (create vs. edit) — role CREATE and
  // role UPDATE have no override or exposed grant today, so both are
  // effectively Admin-only, but they're checked as separate actions in
  // case that ever changes independently.
  const canEditRoleFields =
    !readOnly && (roleId ? canManageRolePermissions : canCreateRole);

  const onHide = () => {
    closeModal({ modalName: modals.role });
    setEnableEditRoleDescription(false);
    setEnableEditRoleTitle(false);
    setUsersWithRole([]);
    setRoleRequirements([]);
    refetchTable();
  };
  const [usersWithRole, setUsersWithRole] = useState([]);

  const [enableEditRoleTitle, setEnableEditRoleTitle] = useState(false);
  const [enableEditRoleDescription, setEnableEditRoleDescription] =
    useState(false);
  const [roleRequirements, setRoleRequirements] = useState([]);
  const {
    refetch,
    data: { roles: [role] = [{}], requirements: allRequirements = [] } = {},
  } = useQuery(rolesQuery, {
    variables: {
      options: {
        filters: [{ field: 'id', operator: 'eq', value: `${roleId}` }],
        sort: [{ field: 'title', order: 'asc' }],
      },
    },
    skip: !roleId || !show,
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (!!data) {
        setUsersWithRole(data.roles?.[0]?.users || []);
        setRoleRequirements(
          data.roles?.[0]?.requirements?.map((r) => ({
            value: r.id,
            label: r.title,
            ...r,
          })),
        );
      }
    },
  });

  const convertToCreatableOptions = (requirements) => {
    return (
      requirements?.map((requirement) => ({
        value: requirement.id,
        label: requirement.title,
      })) || []
    );
  };

  const onBlurRoleInput = () => {
    if (!!roleId) {
      editRole({
        roleId,
        title: roleTitle,
        description: roleDescription,
      }).then(() => {
        setEnableEditRoleDescription(false);
        setEnableEditRoleTitle(false);
      });
    }
  };

  useEffect(() => {
    if (enableEditRoleTitle && !!roleTitleRef.current) {
      roleTitleRef.current.focus();
    }
    if (enableEditRoleDescription && !!roleDescriptionRef.current) {
      roleDescriptionRef.current.focus();
    }
  }, [enableEditRoleTitle, enableEditRoleDescription]);

  const handleRequirementsChange = async (selectedOptions) => {
    const selectedIds = (selectedOptions || []).map((option) => option.value);
    const currentIds = (role?.requirements || []).map((r) => r.id);

    const added = selectedIds.filter((id) => !currentIds.includes(id));
    const removed = currentIds.filter((id) => !selectedIds.includes(id));

    await Promise.all([
      ...added.map((id) =>
        associateRoleRequirement({
          variables: { roleId, requirementId: id },
        }),
      ),
      ...removed.map((id) =>
        disassociateRoleRequirement({
          variables: { roleId, requirementId: id },
        }),
      ),
    ]);
    refetch();
  };

  return (
    <Modal
      open={show}
      onClose={onHide}
      title={`${title || 'Update Role'} 💼`}
      hideCancel
      hideSubmit={roleId}
      submitDisabled={!roleTitle || (!roleId && !canCreateRole)}
      disableCloseOnSubmit={!role?.id}
      onSubmit={() => {
        addRole({
          variables: { title: roleTitle, description: roleDescription },
        }).then(
          ({
            data: {
              addRole: { id },
            },
          }) => {
            update({
              roleId: id,
              roleTitle,
              roleDescription,
            });
          },
        );
      }}
      className={styles.modalMinWidth}
    >
      <div className={styles.container}>
        <div className={styles.editContainer}>
          {canEditRoleFields && (!roleId || enableEditRoleTitle) ? (
            <Form.Control
              required
              type="text"
              placeholder={`Enter Role Title ${t(
                keys.roles.ROLE_NAME_INPUT_PLACEHOLDER,
              )}`}
              value={roleTitle}
              ref={roleTitleRef}
              onChange={(e) => {
                update({
                  roleTitle: e.target.value,
                });
              }}
              onBlur={() => {
                onBlurRoleInput();
              }}
              size="sm"
            />
          ) : (
            <Text noMargin weight="semibold" size="md-lg">
              {roleTitle}
            </Text>
          )}
          {canEditRoleFields && !enableEditRoleTitle && !!role?.id && (
            <Icon
              className={styles.editIcon}
              baseClassName="material-icons-outlined"
              onClick={() => setEnableEditRoleTitle(true)}
              style={{ fontSize: '1.2rem' }}
            >
              edit
            </Icon>
          )}
        </div>
        <div className={styles.spacerSm} />
        <div className={styles.editContainer}>
          {canEditRoleFields && (!roleId || enableEditRoleDescription) ? (
            <Form.Control
              required
              rows={2}
              as="textarea"
              placeholder={`Enter Role Description ${t(
                keys.roles.ROLE_DESCRIPTION_INPUT_PLACEHOLDER,
              )}`}
              value={roleDescription}
              ref={roleDescriptionRef}
              onChange={(e) => {
                update({
                  roleDescription: e.target.value,
                });
              }}
              onBlur={() => {
                onBlurRoleInput();
              }}
              size="sm"
            />
          ) : (
            <Text
              noMargin
              color={!!roleDescription ? 'primary' : 'secondaryLight'}
            >
              {roleDescription || 'No description'}
            </Text>
          )}

          {canEditRoleFields && !enableEditRoleDescription && !!role?.id && (
            <Icon
              className={styles.editIcon}
              baseClassName="material-icons-outlined"
              onClick={() => setEnableEditRoleDescription(true)}
              style={{ fontSize: '1.2rem' }}
            >
              edit
            </Icon>
          )}
        </div>

        <br />
        {!!roleId && (
          <div>
            <div className={styles.titleContainer}>
              <div>
                <Text noMargin weight="semiBold">
                  Reports To
                </Text>
                <Text noMargin color="secondary" size="sm">
                  The role this position reports up to
                </Text>
              </div>
            </div>
            <div className={styles.spacerSm} />
            {!canEditRoleFields ? (
              <Text
                noMargin
                color={role?.reportTo ? 'primary' : 'secondaryLight'}
              >
                {role?.reportTo?.title || 'No reporting role set'}
              </Text>
            ) : (
              <UniversalDropdown
                isClearable
                placeholder="Role"
                selected={role?.reportTo || null}
                options={allRoles.filter((r) => r.id !== role?.id)}
                onChange={(value) => {
                  editRole({
                    roleId,
                    reportToId: value?.id ?? null,
                    onComplete: refetch,
                  });
                }}
              />
            )}
            <br />
            <div className={styles.titleContainer}>
              <div>
                <Text noMargin weight="semiBold">
                  {`Certifications`}
                </Text>
                <Text noMargin color="secondary" size="sm">
                  {t(keys.permissions.ADD_REQUIREMENT)}
                </Text>
              </div>
            </div>
            <div className={styles.spacerSm} />
            <div className={styles.addRequirementContainer}>
              {readOnly || !canManageRequirements ? (
                <Text
                  noMargin
                  color={
                    roleRequirements?.length ? 'primary' : 'secondaryLight'
                  }
                >
                  {roleRequirements?.length
                    ? roleRequirements.map((r) => r.label).join(', ')
                    : 'No certifications set'}
                </Text>
              ) : (
                <CreatableSelect
                  isMulti
                  placeholder={t(keys.action.SEARCH, {
                    variable: t(keys.roles.REQUIREMENTS_TITLE),
                  })}
                  closeMenuOnSelect={false}
                  isClearable
                  options={convertToCreatableOptions(allRequirements)}
                  value={roleRequirements}
                  onCreateOption={async (value) => {
                    const {
                      data: { addRequirement: newRequirement },
                    } = await addRequirement({
                      variables: {
                        title: value,
                      },
                    });
                    await associateRoleRequirement({
                      variables: {
                        roleId,
                        requirementId: newRequirement.id,
                      },
                    });
                    update({
                      requirementTitle: '',
                      requirementDescription: '',
                    });
                    refetch();
                  }}
                  onChange={handleRequirementsChange}
                  className={styles.multiSelector}
                />
              )}
            </div>
            <Text noMargin weight="semiBold">
              Users
            </Text>
            <Spacer size="xs" />
            <UserMultiSelector
              type="all"
              readOnly={!canEditRoleFields}
              selected={usersWithRole}
              onUserAdded={(user) => {
                setUsersWithRole([...usersWithRole, user]);
                addRoleToUser({ userId: user.id, roleId, onComplete: refetch });
              }}
              onUserRemoved={(user) => {
                setUsersWithRole(usersWithRole.filter((u) => u.id !== user.id));
                removeRoleFromUser({
                  userId: user.id,
                  roleId,
                  onComplete: refetch,
                });
              }}
            />
            <br />
            <div>
              <PermissionManager
                parent={'modal'}
                show={true}
                role={role}
                roles={[role]}
                currentPermissions={rolePermissions || []}
                updateMutation={editRole}
                setResult={({ updateRole: { permissions } }) =>
                  update({ rolePermissions: permissions })
                }
                padding="sm"
                readOnly={readOnly || !canManageRolePermissions}
              />
            </div>
            <br />
            <div className={styles.buttons}>
              {isAdmin && (
                <Button
                  onClick={() =>
                    openConfirmationModal({
                      variant: 'warning',
                      title: t(keys.action.DELETE_VARIABLE, {
                        variable: t(keys.common.ROLE),
                      }),
                      description: t(keys.action.DELETE_CONFIRMATION, {
                        variable: `${roleTitle} ${t(keys.common.ROLE)}`,
                      }),
                      onSubmit: () => {
                        deleteRole({
                          variables: { roleId },
                        }).then(() => refetch());
                        onHide();
                      },
                    })
                  }
                  value={t(keys.action.DELETE_VARIABLE, {
                    variable: t(keys.common.ROLE),
                  })}
                  icon={'delete_outlined'}
                  variant="danger"
                  align={'right'}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
